import { openDB, type DBSchema, type IDBPObjectStore } from 'idb';
import { captureOfflinePrivacyResetEpoch, offlinePrivacyResetEpochIsCurrent } from './offline-privacy-reset.ts';
import type { OfflineCacheScope } from './offline-cache.ts';
export interface ChatPending {
  chatId: string;
  audienceVersion?: number;
  body: string;
  clientMessageId: string;
  uploadId: string;
  attachmentIds: string[];
  code?: string;
  file: File | null;
}
interface Entry extends ChatPending {
  key: string;
  scope: string;
  createdAt: number;
}
interface Schema extends DBSchema {
  pending: { key: string; value: Entry };
}
let scope: string | null = null;
let generation = 0;
const database = () =>
  openDB<Schema>('blasto-job-chat-outbox', 1, {
    upgrade(db) {
      db.createObjectStore('pending', { keyPath: 'key' });
    },
  });
export function setChatOutboxScope(value: OfflineCacheScope | null) {
  const next = value ? JSON.stringify(value) : null;
  if (scope !== next) {
    scope = next;
    generation++;
  }
}
export function captureChatOutbox() {
  const capturedScope = scope,
    capturedGeneration = generation,
    epoch = captureOfflinePrivacyResetEpoch();
  const current = () =>
    !!capturedScope &&
    !!epoch &&
    scope === capturedScope &&
    generation === capturedGeneration &&
    offlinePrivacyResetEpochIsCurrent(epoch);
  async function operate<T>(
    write: boolean,
    action: (store: IDBPObjectStore<Schema, ['pending'], 'pending', 'readwrite'>) => Promise<T>,
  ): Promise<T> {
    if (!current()) throw new Error('Chat session changed');
    const db = await database();
    try {
      if (!current()) throw new Error('Chat session changed');
      const tx = db.transaction('pending', 'readwrite');
      try {
        const result = await action(tx.store);
        if (!current()) {
          tx.abort();
          throw new Error('Chat session changed');
        }
        await tx.done;
        if (!current()) throw new Error('Chat session changed');
        return result;
      } catch (error) {
        try {
          tx.abort();
        } catch {}
        await tx.done.catch(() => {});
        throw error;
      }
    } finally {
      db.close();
    }
  }
  return {
    current,
    list: () =>
      operate(false, async (store) =>
        ((await store.getAll()) as Entry[])
          .filter((e) => e.scope === capturedScope)
          .sort((a, b) => a.createdAt - b.createdAt),
      ),
    save: (entry: ChatPending) =>
      operate(true, async (store) => {
        const key = JSON.stringify([capturedScope, entry.clientMessageId]);
        const old = (await store.get(key)) as Entry | undefined;
        const all = (await store.getAll()) as Entry[];
        const size = (e: ChatPending) => (e.file?.size ?? 0) + new TextEncoder().encode(e.body).length + 2048;
        if (
          (entry.file?.size ?? 0) > 10 * 1024 * 1024 ||
          (!old && all.length >= 15) ||
          all.filter((e) => e.key !== key).reduce((n, e) => n + size(e), 0) + size(entry) > 25 * 1024 * 1024
        )
          throw new Error('Chat outbox is full');
        await store.put({ ...entry, key, scope: capturedScope!, createdAt: old?.createdAt ?? Date.now() });
      }),
    remove: (id: string) =>
      operate(true, async (store) => {
        await store.delete(JSON.stringify([capturedScope, id]));
      }),
  };
}
export async function clearChatOutbox(): Promise<boolean> {
  scope = null;
  generation++;
  try {
    const db = await database();
    try {
      await db.clear('pending');
    } finally {
      db.close();
    }
    return true;
  } catch {
    return false;
  }
}
